import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { getProducts } from '../api/products';
import { getIngredients } from '../api/ingredients';
import { getUsers } from '../api/users';
import type { Breadcrumb } from '../hooks/use-route';

interface TopbarProps {
  breadcrumbs: Breadcrumb[];
  onNavigate: (hash: string) => void;
  onMenuToggle: () => void;
  onOpenCommandPalette: () => void;
}

interface SearchResult {
  type: 'product' | 'ingredient' | 'user';
  label: string;
  sublabel: string;
  id: string;
}

function formatTime() {
  return new Date().toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

export function Topbar({ breadcrumbs, onNavigate, onMenuToggle, onOpenCommandPalette }: TopbarProps) {
  const [time, setTime] = useState(formatTime);
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [searching, setSearching] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const id = setInterval(() => setTime(formatTime()), 30_000);
    return () => clearInterval(id);
  }, []);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const doSearch = useCallback(async (q: string) => {
    if (!q.trim()) {
      setResults([]);
      setShowResults(false);
      return;
    }

    setSearching(true);
    const items: SearchResult[] = [];

    try {
      const [prods, ings, usrs] = await Promise.allSettled([
        getProducts({ page: 1, limit: 5, search: q }),
        getIngredients({ page: 1, limit: 5, search: q }),
        getUsers({ page: 1, limit: 5, search: q }),
      ]);

      if (prods.status === 'fulfilled') {
        for (const p of prods.value.products) {
          items.push({ type: 'product', label: p.name ?? p.barcode, sublabel: p.barcode, id: p.barcode });
        }
      }
      if (ings.status === 'fulfilled') {
        for (const i of ings.value.ingredients) {
          items.push({ type: 'ingredient', label: i.canonical_name, sublabel: i.e_number ?? '', id: String(i.id) });
        }
      }
      if (usrs.status === 'fulfilled') {
        for (const u of usrs.value.users) {
          items.push({ type: 'user', label: u.id, sublabel: `${u.scan_count} scans`, id: u.id });
        }
      }
    } catch {
      // ignore search errors
    }

    setResults(items);
    setShowResults(items.length > 0);
    setSearching(false);
    setActiveIdx(-1);
  }, []);

  const handleInput = useCallback((value: string) => {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(value), 300);
  }, [doSearch]);

  const handleResultClick = useCallback((r: SearchResult) => {
    setShowResults(false);
    setSearchQuery('');
    if (r.type === 'product') onNavigate(`catalog/products/${r.id}`);
    else if (r.type === 'ingredient') onNavigate(`catalog/ingredients/${r.id}`);
    else if (r.type === 'user') onNavigate(`catalog/users/${r.id}`);
  }, [onNavigate]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((prev) => Math.max(prev - 1, -1));
    } else if (e.key === 'Escape') {
      setShowResults(false);
      inputRef.current?.blur();
    } else if (e.key === 'Enter' && activeIdx >= 0 && results[activeIdx]) {
      e.preventDefault();
      handleResultClick(results[activeIdx]);
    }
  }, [results.length, activeIdx, handleResultClick]);

  return (
    <div class="topbar">
      <button class="mobile-menu-toggle" onClick={onMenuToggle}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="4" y1="12" x2="20" y2="12" />
          <line x1="4" y1="6" x2="20" y2="6" />
          <line x1="4" y1="18" x2="20" y2="18" />
        </svg>
      </button>

      {/* Breadcrumbs */}
      <div class="topbar-breadcrumbs">
        {breadcrumbs.map((crumb, i) => {
          const isLast = i === breadcrumbs.length - 1;
          return (
            <>
              {i > 0 && <span class="topbar-breadcrumb-sep">/</span>}
              {crumb.hash && !isLast ? (
                <button
                  class="topbar-breadcrumb-link"
                  onClick={() => onNavigate(crumb.hash!)}
                >
                  {crumb.label}
                </button>
              ) : (
                <span>{crumb.label}</span>
              )}
            </>
          );
        })}
      </div>

      {/* Global Search */}
      <div class="topbar-search" ref={wrapRef}>
        <svg class="topbar-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <input
          ref={inputRef}
          class="topbar-search-input"
          type="text"
          placeholder="Search products, ingredients, users..."
          value={searchQuery}
          onInput={(e) => handleInput((e.target as HTMLInputElement).value)}
          onFocus={() => { if (results.length > 0) setShowResults(true); }}
          onKeyDown={handleKeyDown}
        />
        <span
          class="topbar-search-kbd"
          onClick={onOpenCommandPalette}
          style={{ cursor: 'pointer' }}
        >
          {navigator.platform.includes('Mac') ? '\u2318K' : 'Ctrl+K'}
        </span>

        {showResults && (
          <div class="topbar-search-results">
            {results.length === 0 && !searching ? (
              <div class="topbar-search-empty">No results</div>
            ) : (
              results.map((r, i) => (
                <div
                  key={`${r.type}-${r.id}`}
                  class={`topbar-search-item ${i === activeIdx ? 'active' : ''}`}
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => handleResultClick(r)}
                >
                  <span class="topbar-search-item-type">{r.type}</span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.label}
                  </span>
                  {r.sublabel && (
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>
                      {r.sublabel}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <div class="topbar-meta">
        <span>{time}</span>
      </div>
    </div>
  );
}
